/**
 * The outline: which headings a document has, and what number each one carries.
 *
 * COMPUTED, NEVER STORED. A heading's number is its POSITION — "4.2" is not a fact about that
 * heading, it is a fact about how many level-1 headings precede it and how many level-2 headings
 * follow the last of those. Store the number and it is wrong the moment somebody inserts a section
 * above it, and wrong silently: the document still reads as though it means something.
 *
 * THE RULE IS WRITTEN ONCE HERE AND MIRRORED EXACTLY IN document_rendition.py. The same discipline
 * as tab stops, for the same reason: a cross-reference that says "4.2" on screen and "4.3" in the
 * signed PDF is worse than having no cross-references. If this function changes, that one changes
 * in the same commit.
 *
 * A SKIPPED LEVEL DOES NOT SKIP A NUMBER. An h3 directly under an h1 is numbered 1.1, not 1.0.1:
 * people skip levels by accident constantly, and a numbering scheme that punishes them by emitting
 * something that looks like a bug is not helping.
 *
 * AND THE LEVEL A HEADING WAS DECIDES WHERE IT BELONGS, not how deep the stack happens to be when
 * it arrives. Tracking only the depth made two h3s under an h1 nest instead of being siblings --
 * the first clamped to depth 2, and the second then saw a deeper stack and clamped to depth 3.
 * So the raw level each counter stands for is carried alongside it.
 */

/** The deepest level that takes a number. Beyond this a heading is a label, not a clause. */
export const MAX_NUMBERED_LEVEL = 4;

export type OutlineEntry = {
    /** The heading's stable id, when it has one. Null until something needs to point at it. */
    anchor: string | null;
    level: number;
    /** "4.2" — empty when the document is not numbered. */
    number: string;
    text: string;
};

/** Everything inside a node, as the text a reader sees. */
function textOf(node: any): string {
    if (!node) return "";
    if (node.type === "text") return node.text ?? "";
    return (node.content ?? []).map(textOf).join("");
}

/**
 * Walk the document and number its headings.
 *
 * TOP LEVEL ONLY. A heading inside a table cell or a list item is not a section of the procedure,
 * and numbering it would put "4.3" inside a row.
 */
export function outlineOf(body: any, numbered = true): OutlineEntry[] {
    const entries: OutlineEntry[] = [];
    const counters: number[] = [];
    const openLevels: number[] = [];

    for (const node of body?.content ?? []) {
        if (node?.type !== "heading") continue;
        const attrs = node.attrs ?? {};
        const raw = Math.max(1, Math.min(MAX_NUMBERED_LEVEL, Number(attrs.level) || 1));

        while (openLevels.length && openLevels[openLevels.length - 1] > raw) {
            openLevels.pop();
            counters.pop();
        }
        if (openLevels.length && openLevels[openLevels.length - 1] === raw) {
            counters[counters.length - 1] += 1;        // a sibling of the heading before it
        } else {
            openLevels.push(raw);                      // one step deeper, however far it jumped
            counters.push(1);
        }

        entries.push({
            anchor: attrs.anchor ?? null,
            level: counters.length,
            number: numbered ? counters.join(".") : "",
            text: textOf(node).trim(),
        });
    }

    return entries;
}

/** The entry one anchor points at, or null when the heading it named has gone. */
export function entryFor(body: any, anchor: string, numbered = true): OutlineEntry | null {
    return outlineOf(body, numbered).find(one => one.anchor === anchor) ?? null;
}

export type Bookmark = {
    /** The id a reference points at. */
    id: string;
    /** What somebody called it. This is what a reference by title reads as. */
    name: string;
    /** The number of the SECTION it sits in — see bookmarksOf. */
    number: string;
};

/**
 * Every bookmark in the document, with the section each one sits in.
 *
 * A BOOKMARK'S NUMBER IS ITS SECTION'S NUMBER, and that is the decision that makes bookmarks worth
 * having. "See 4.2" is what a procedure says about a passage; a passage has no number of its own,
 * and inventing one — 4.2a, or a count of bookmarks — would be a numbering scheme nobody asked for
 * and no reader could check against the page. So a reference to a bookmark by number says which
 * section to turn to, and by title says what to look for once there.
 *
 * A BOOKMARK BEFORE THE FIRST HEADING has no section, and its number is empty. A reference to it
 * by number falls back to the name, the same way a reference into an unnumbered document does.
 */
export function bookmarksOf(body: any, numbered = true): Bookmark[] {
    const found: Bookmark[] = [];
    const counters: number[] = [];
    const openLevels: number[] = [];

    const collect = (node: any, section: string) => {
        if (!node || typeof node !== "object") return;
        for (const mark of node.marks ?? []) {
            if (mark?.type !== "bookmark" || !mark.attrs?.id) continue;
            if (found.some(one => one.id === mark.attrs.id)) continue;   // a mark split by editing
            found.push({ id: mark.attrs.id, name: mark.attrs.name ?? "", number: section });
        }
        (node.content ?? []).forEach((child: any) => collect(child, section));
    };

    for (const node of body?.content ?? []) {
        if (node?.type === "heading") {
            const raw = Math.max(1, Math.min(MAX_NUMBERED_LEVEL, Number(node.attrs?.level) || 1));
            while (openLevels.length && openLevels[openLevels.length - 1] > raw) {
                openLevels.pop();
                counters.pop();
            }
            if (openLevels.length && openLevels[openLevels.length - 1] === raw) {
                counters[counters.length - 1] += 1;
            } else {
                openLevels.push(raw);
                counters.push(1);
            }
        }
        // THE SECTION AS IT STANDS AT THIS BLOCK, which is why this walk cannot reuse outlineOf:
        // that one reports the headings, and this needs to know which heading each passage is
        // underneath.
        collect(node, numbered && counters.length ? counters.join(".") : "");
    }

    return found;
}

/** What a reference shows. The document stores the choice; the words come from the outline. */
export type ReferenceShows = "number" | "title" | "both";

/** What a reference reads as when the heading it pointed at is no longer there. */
export const MISSING = "[reference missing]";

/**
 * What one cross-reference reads as, given the document it sits in.
 *
 * HERE RATHER THAN IN CrossReference.ts, for two reasons. It belongs beside the rule it depends on
 * -- the renderer keeps _reference_text beside _outline for the same reason -- and this file has no
 * editor imports, so it can be tested without Jest having to parse @tiptap/core.
 */
export function referenceText(body: any, target: string, shows: ReferenceShows,
                              numbered: boolean): string {
    const entry = outlineOf(body, numbered).find(one => one.anchor === target)
        // A BOOKMARK ANSWERS THE SAME QUESTION AS A HEADING -- where is this, and what is it called
        // -- so one reference node points at either and nothing above here needs to know which.
        ?? (() => {
            const mark = bookmarksOf(body, numbered).find(one => one.id === target);
            return mark ? { anchor: mark.id, level: 1, number: mark.number, text: mark.name } : null;
        })();
    if (!entry) return MISSING;
    if (shows === "title") return entry.text || MISSING;
    if (shows === "both") return entry.number ? `${entry.number} ${entry.text}` : entry.text;
    // A REFERENCE BY NUMBER IN AN UNNUMBERED DOCUMENT has no number to show, so it falls back to
    // the title rather than rendering an empty span nobody can see or click.
    return entry.number || entry.text || MISSING;
}

/** Whether this document asks for its headings to be numbered. Off unless somebody turned it on. */
export function isNumbered(body: any): boolean {
    return Boolean((body?.attrs ?? {}).outlineNumbering);
}
